const HUBSPOT_CONFIG_ID = 'hubspot-newsletter-config';
const HUBSPOT_API_BASE = 'https://api.hsforms.com/submissions/v3/integration/submit';
const HUBSPOT_COOKIE_NAME = 'hubspotutk';
const HUBSPOT_PROCESSING_CONSENT_TEXT = 'I agree to allow InkPoster to process my personal data.';
let sectionListenerAttached = false;

function logDebug(config, message, details) {
  if (!config?.debugMode) {
    return;
  }

  if (details !== undefined) {
    console.debug('[HubSpot Newsletter]', message, details);

    return;
  }

  console.debug('[HubSpot Newsletter]', message);
}

function parseJson(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function getCookieValue(name) {
  if (!name || !document.cookie) {
    return null;
  }

  const cookies = document.cookie.split(';').map((cookie) => cookie.trim());
  const targetCookie = cookies.find((cookie) => cookie.startsWith(`${name}=`));

  if (!targetCookie) {
    return null;
  }

  const cookieValue = targetCookie.slice(name.length + 1);

  return cookieValue || null;
}

function parseSubscriptionTypeId(subscriptionTypeId) {
  if (subscriptionTypeId === null || typeof subscriptionTypeId === 'undefined') {
    return null;
  }

  const normalizedValue = String(subscriptionTypeId).trim();

  if (!normalizedValue) {
    return null;
  }

  const numericValue = Number(normalizedValue);

  if (!Number.isInteger(numericValue) || numericValue <= 0) {
    return null;
  }

  return numericValue;
}

function normalizeIdentifier(value) {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return null;
  }

  const normalizedValue = String(value).trim();

  if (!normalizedValue) {
    return null;
  }

  return normalizedValue;
}

function buildPayload({ email, subscriptionTypeId, communicationValue, trackingEnabled }) {
  const hubspotUserToken = trackingEnabled ? getCookieValue(HUBSPOT_COOKIE_NAME) : null;

  const payload = {
    submittedAt: Date.now(),
    fields: [{ name: 'email', value: email }],
    context: {
      pageUri: globalThis.location.href,
      pageName: document.title,
    },
  };

  if (hubspotUserToken) {
    payload.context.hutk = hubspotUserToken;
  }

  payload.legalConsentOptions = {
    consent: {
      consentToProcess: true,
      text: HUBSPOT_PROCESSING_CONSENT_TEXT,
      communications: [
        {
          value: communicationValue,
          subscriptionTypeId,
          text: 'Newsletter',
        },
      ],
    },
  };

  return payload;
}

function sendHubspotSubmission(config, formElement) {
  const portalId = normalizeIdentifier(config?.portalId);
  const formId = normalizeIdentifier(config?.formId);

  if (!portalId || !formId) {
    logDebug(config, 'Submit skipped because portalId/formId is missing or invalid.', {
      rawPortalId: config?.portalId,
      rawFormId: config?.formId,
    });

    return;
  }

  const emailInput = formElement.querySelector('input[name="contact[email]"]');
  const trimmedEmail = emailInput?.value?.trim();

  if (!emailInput || !trimmedEmail) {
    logDebug(config, 'Submit skipped because email is empty.');

    return;
  }

  const consentCheckbox = formElement.querySelector('input[name="contact[accepts_marketing]"]');

  if (!consentCheckbox) {
    logDebug(config, 'Submit skipped because consent checkbox is missing from form.');

    return;
  }

  if (!consentCheckbox.checked) {
    logDebug(config, 'Submit skipped because consent checkbox is not checked.');

    return;
  }

  const parsedSubscriptionTypeId = parseSubscriptionTypeId(config.subscriptionTypeId);

  if (!parsedSubscriptionTypeId) {
    logDebug(config, 'Submit skipped because subscriptionTypeId is missing or invalid.', {
      rawSubscriptionTypeId: config.subscriptionTypeId,
    });

    return;
  }

  const endpointUrl = `${HUBSPOT_API_BASE}/${portalId}/${formId}`;

  const requestPayload = buildPayload({
    email: trimmedEmail,
    subscriptionTypeId: parsedSubscriptionTypeId,
    communicationValue: consentCheckbox.checked,
    trackingEnabled: config.trackingEnabled ?? false,
  });

  logDebug(config, 'Submitting payload to HubSpot.', {
    endpointUrl,
    communicationValue: consentCheckbox.checked,
  });

  void fetch(endpointUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestPayload),
    keepalive: true,
  })
    .then((response) => {
      logDebug(config, 'HubSpot submission completed.', {
        status: response.status,
        ok: response.ok,
      });
    })
    .catch((error) => {
      logDebug(config, 'HubSpot submission failed.', {
        error: error instanceof Error ? error.message : String(error),
      });
    });
}

function parseConfig() {
  const configScript = document.getElementById(HUBSPOT_CONFIG_ID);

  if (!configScript) {
    return null;
  }

  return parseJson(configScript.textContent);
}

function bindNewsletterForms(config) {
  const newsletterForms = document.querySelectorAll('.newsletter-form');

  logDebug(config, 'Binding newsletter forms.', {
    formsCount: newsletterForms.length,
  });

  newsletterForms.forEach((formElement) => {
    if (formElement.dataset.hubspotNewsletterBound === 'true') {
      return;
    }

    formElement.dataset.hubspotNewsletterBound = 'true';
    formElement.addEventListener('submit', () => {
      sendHubspotSubmission(config, formElement);
    });
  });
}

export function initHubspotNewsletter() {
  const config = parseConfig();
  const portalId = normalizeIdentifier(config?.portalId);
  const formId = normalizeIdentifier(config?.formId);

  if (!portalId || !formId) {
    logDebug(config, 'Initialization skipped due to missing config.', {
      hasPortalId: Boolean(portalId),
      hasFormId: Boolean(formId),
    });

    return;
  }

  logDebug(config, 'Initialization started.', {
    debugMode: config.debugMode,
  });

  bindNewsletterForms(config);

  if (sectionListenerAttached) {
    logDebug(config, 'Section listener already attached.');

    return;
  }

  sectionListenerAttached = true;

  document.addEventListener('shopify:section:load', () => {
    logDebug(config, 'shopify:section:load detected. Rebinding forms.');
    bindNewsletterForms(config);
  });
}

initHubspotNewsletter();
